import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Play,
  Square,
  SkipForward,
  BarChart3,
  Clock,
  Camera,
  CameraOff,
  Mic,
  MicOff,
  CheckCircle,
  MessageSquare,
  Video,
  Volume2,
  RefreshCw,
} from "lucide-react";
import { interviewActions } from "../../../Redux/ActionCreator.js";
import { ApiService } from "../api.service.jsx";
import LoadingOverlay from "../common/LoadingOverlay.jsx";
import ErrorMessage from "../common/ErrorMessage.jsx";
import ProgressBar from "../common/ProgressBar.jsx";
import Timer from "../common/Timer.jsx";
import VideoPreview from "../common/VideoPreview.jsx";
import BackButton from "../../common/back_button/index.jsx";
import { ToastContainer, toast } from "react-toastify";

const api = new ApiService();

const InterviewSessionPage = () => {
  const dispatch = useDispatch();
  const flowLanguage = useSelector((e) => e.t_lang.selected);
  const {
    questions,
    currentQuestionIndex,
    interviewData,
    recording,
    analytics,
    ui,
  } = useSelector((state) => state.interview);

  const { darkMode, cameraEnabled, micEnabled } = ui.settings;
  const { loading, error } = ui;
  const { completedQuestions } = analytics;

  const mediaRecorderRef = useRef(null);
  const videoRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordingBlobRef = useRef(null);

  let [currentQuestion, setCurrentQuestion] = useState(
    questions[currentQuestionIndex]
  );

  // Add this state at the top of your component
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [pendingRecordingStart, setPendingRecordingStart] = useState(false);

  const theme = {
    bg: darkMode
      ? "bg-gray-900"
      : "bg-gradient-to-br from-blue-50 to-indigo-100",
    card: darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
    text: darkMode ? "text-gray-100" : "text-gray-900",
    textSecondary: darkMode ? "text-gray-400" : "text-gray-600",
    button: darkMode
      ? "bg-blue-600 hover:bg-blue-700"
      : "bg-blue-600 hover:bg-blue-700",
    buttonSecondary: darkMode
      ? "bg-gray-700 hover:bg-gray-600"
      : "bg-gray-100 hover:bg-gray-200",
  };

  // Timer effect
  useEffect(() => {
    let interval;
    if (
      recording.isRecording &&
      !recording.isPaused &&
      recording.timeLeft > 0
    ) {
      interval = setInterval(() => {
        dispatch(interviewActions.setTimer(recording.timeLeft - 1));
        if (recording.timeLeft <= 1) {
          handleStopRecording();
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [recording.isRecording, recording.isPaused, recording.timeLeft, dispatch]);

  // Check for existing analytics when question changes
  useEffect(() => {
    if (currentQuestion && questions.length > 0) {
      // Check if this question already has analytics
      const questionWithAnalytics = questions.find(
        (q) => q._id === currentQuestion._id && q.analytics !== undefined
      );

      if (questionWithAnalytics && questionWithAnalytics.analytics) {
        dispatch(interviewActions.setCurrentAnalytics(questionWithAnalytics));
        dispatch(interviewActions.setCurrentStep("analytics"));
      }
    }
  }, [currentQuestionIndex, questions, currentQuestion, dispatch]);

  useEffect(() => {
    // Check if there's a saved audio URL for the current question
    const savedAudioUrl = recording.questionAudioUrls?.[currentQuestionIndex];
    if (savedAudioUrl && !recording.audioUrl) {
      dispatch(interviewActions.setAudioUrl(savedAudioUrl));
    }
  }, [
    currentQuestionIndex,
    recording.questionAudioUrls,
    recording.audioUrl,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      // Cleanup blob URLs on component unmount to prevent memory leaks
      Object.values(recording.questionAudioUrls || {}).forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const setupMediaStream = useCallback(async () => {
    try {
      const constraints = {
        video: cameraEnabled
          ? {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              facingMode: "user",
            }
          : false,
        audio: micEnabled
          ? {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            }
          : false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      return stream;
    } catch (error) {
      console.error("Error accessing media devices:", error);
      // dispatch(
      //   interviewActions.setError(
      //     "Could not access camera/microphone. Please check permissions."
      //   )
      // );
      toast.warn(
        "Could not access camera/microphone. Please check permissions.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
      return null;
    }
  }, [cameraEnabled, micEnabled, dispatch]);

  const handleConfirmOverwrite = () => {
    // Clean up the existing audio URL to free memory
    const existingUrl =
      recording.questionAudioUrls?.[currentQuestionIndex] || recording.audioUrl;
    if (existingUrl) {
      URL.revokeObjectURL(existingUrl);
    }

    setShowOverwriteConfirm(false);
    setPendingRecordingStart(true);
    // Trigger the actual recording
    handleStartRecording();
  };

  const handleCancelOverwrite = () => {
    setShowOverwriteConfirm(false);
    setPendingRecordingStart(false);
  };

  // Reset the pending flag
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Make sure we have a valid result
        if (reader.result) {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to convert blob to base64"));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  };

  const base64ToBlob = async (base64Data) => {
    return new Promise((resolve, reject) => {
      try {
        // Validate input
        if (!base64Data || typeof base64Data !== "string") {
          throw new Error("Invalid base64 data provided");
        }

        // Check if it's a data URL format
        if (!base64Data.includes(",")) {
          throw new Error("Invalid base64 format - missing comma separator");
        }

        const [header, data] = base64Data.split(",");

        if (!header || !data) {
          throw new Error("Invalid base64 format - invalid header or data");
        }

        // Extract MIME type
        const mimeMatch = header.match(/:(.*?);/);
        const mimeType = mimeMatch ? mimeMatch[1] : "audio/webm";

        const byteCharacters = atob(data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        resolve(new Blob([byteArray], { type: mimeType }));
      } catch (error) {
        console.error("Error converting base64 to blob:", error);
        reject(error);
      }
    });
  };

  const handleStartRecording = async () => {
    // Check if there's already a recording for this question
    const existingAudioUrl =
      recording.questionAudioUrls?.[currentQuestionIndex] || recording.audioUrl;

    if (existingAudioUrl && !pendingRecordingStart) {
      // Show confirmation dialog
      setShowOverwriteConfirm(true);
      return;
    }

    setPendingRecordingStart(false);
    try {
      dispatch(interviewActions.clearError());

      // Clear any existing audio URL for this question before starting new recording
      if (recording.audioUrl) {
        // Revoke the old blob URL to free memory
        if (recording.audioUrl.startsWith("blob:")) {
          URL.revokeObjectURL(recording.audioUrl);
        }
        // Clear from Redux state
        dispatch(interviewActions.clearQuestionAudio(currentQuestionIndex));
      }

      const stream = await setupMediaStream();
      if (!stream) return;

      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        // dispatch(interviewActions.setError("No audio track available"));
        toast.warn(
          "Microphone access is required to record your response. Please enable your microphone and try again.",
          {
            position: toast.POSITION.TOP_RIGHT,
          }
        );
        return;
      }

      const audioStream = new MediaStream([audioTracks[0]]);
      mediaRecorderRef.current = new MediaRecorder(audioStream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        recordingBlobRef.current = audioBlob;

        const base64Audio = await blobToBase64(audioBlob);

        // const audioUrl = URL.createObjectURL(audioBlob);
        dispatch(interviewActions.setAudioUrl(base64Audio));
        dispatch(interviewActions.markQuestionCompleted(currentQuestionIndex));
      };

      mediaRecorderRef.current.start();
      dispatch(interviewActions.startRecording());
      dispatch(interviewActions.setTimer(currentQuestion?.timeLimit || 90));
    } catch (error) {
      // console.error("Error starting recording:", error);
      // dispatch(interviewActions.setError("Failed to start recording"));
      toast.error("Failed to start recording", {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  };

  // In sessionPage.jsx - Update handleStopRecording with proper null checks
  const handleStopRecording = () => {
    try {
      // Check if mediaRecorder exists and is in a valid state
      if (mediaRecorderRef.current && recording.isRecording) {
        // Check the recorder state before stopping
        if (
          mediaRecorderRef.current.state === "recording" ||
          mediaRecorderRef.current.state === "paused"
        ) {
          mediaRecorderRef.current.stop();
        }
        dispatch(interviewActions.stopRecording());

        // Stop all media tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }
      } else if (recording.isRecording) {
        // If recording state is true but no recorder, just update the state
        // console.warn("Recording state is true but no media recorder found");
        dispatch(interviewActions.stopRecording());
      }
    } catch (error) {
      // console.error("Error stopping recording:", error);
      // Force stop the recording state even if there's an error
      dispatch(interviewActions.stopRecording());
      toast.error("Error stopping recording", {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  };

  const uploadAudioAndGetAnalytics = async () => {
    if (!recording.audioUrl && !recordingBlobRef.current) {
      toast.error("No recording available to analyze", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    if (!interviewData) {
      toast.error("No interview data available", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    try {
      dispatch(interviewActions.uploadAudioStart());

      let audioBlob;

      // Handle different audio storage formats
      if (recordingBlobRef.current) {
        audioBlob = recordingBlobRef.current;
        console.log("Using blob from ref:", audioBlob.size, audioBlob.type);
      } else if (recording.audioUrl) {
        console.log("Audio URL type:", typeof recording.audioUrl);
        console.log("Audio URL start:", recording.audioUrl.substring(0, 50));

        if (
          typeof recording.audioUrl === "string" &&
          recording.audioUrl.startsWith("data:")
        ) {
          // Convert base64 to blob
          audioBlob = await base64ToBlob(recording.audioUrl);
          console.log(
            "Converted base64 to blob:",
            audioBlob.size,
            audioBlob.type
          );
        } else if (
          typeof recording.audioUrl === "string" &&
          recording.audioUrl.startsWith("blob:")
        ) {
          // Fetch blob from blob URL
          const response = await fetch(recording.audioUrl);
          audioBlob = await response.blob();
          console.log("Fetched blob from URL:", audioBlob.size, audioBlob.type);
        } else {
          throw new Error("Unsupported audio URL format");
        }
      }

      if (!audioBlob) {
        toast.error("Could not process audio for analysis", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      const uploadData = {
        interviewUniqueCode: interviewData.uniqueCode,
        interviewQuestionsId: interviewData.interviewQuestionsId._id,
        questionId: currentQuestion._id,
        language: flowLanguage,
      };

      await api.uploadAudio(audioBlob, uploadData);

      dispatch(
        interviewActions.setLoadingState(true, "Analyzing your response...")
      );

      // Get analytics
      const analyticsResponse = await api.getAnalytics(
        uploadData.interviewQuestionsId,
        uploadData.questionId,
        flowLanguage,
      );

      if (analyticsResponse.data) {
        dispatch(interviewActions.setCurrentAnalytics(analyticsResponse.data));
        dispatch(interviewActions.addQuestionAnalytics(analyticsResponse.data));
      }

      dispatch(interviewActions.uploadAudioSuccess());
    } catch (error) {
      console.error("Error uploading audio or getting analytics:", error);
      // dispatch(
      //   interviewActions.uploadAudioError(
      //     "Failed to analyze response. Please try again."
      //   )
      // );
      toast.warn("Failed to analyze response. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
      });
      dispatch(interviewActions.setLoadingState(false, ""));
    }
  };

  const handleNextQuestion = () => {
    if (recording.isRecording) {
      handleStopRecording();
      // dispatch(
      //   interviewActions.setError(
      //     "You need to stop recording before moving to the next question"
      //   )
      // );
      toast.error(
        "You need to stop recording before moving to the next question",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
      return;
    }

    if (!analytics.currentQuestionAnalytics) {
      // dispatch(
      //   interviewActions.setError(
      //     "Please get the analytics for the question before moving to the next one"
      //   )
      // );
      toast.error(
        "Please get the analytics for the question before moving to the next one",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
      return;
    }

    if (currentQuestionIndex < questions.length) {
      dispatch(interviewActions.setCurrentQuestion(currentQuestionIndex + 1));
      dispatch(interviewActions.setCurrentStep("interview"));
    } else {
      dispatch(interviewActions.calculateOverallScore());
      dispatch(interviewActions.setCurrentStep("results"));
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex === 0) {
      // dispatch(interviewActions.setError("This is the first question."));
      // dispatch(interviewActions.setCurrentQuestion(currentQuestionIndex - 1));
      dispatch(interviewActions.setCurrentStep("setup"));
    } else {
      dispatch(interviewActions.setCurrentQuestion(currentQuestionIndex - 1));
      dispatch(interviewActions.setCurrentStep("interview"));
    }
  };

  const dismissError = () => {
    dispatch(interviewActions.clearError());
  };

  const toggleCamera = () => {
    dispatch(interviewActions.setCameraEnabled(!cameraEnabled));
  };

  const toggleMic = () => {
    dispatch(interviewActions.setMicEnabled(!micEnabled));
  };

  if (!currentQuestion) {
    return <div>Loading question...</div>;
  }

  const handleReset = () => {
    mediaRecorderRef.current = null;
    videoRef.current = null;
    // Clear current audio URL and blob reference
    dispatch(interviewActions.setAudioUrl(null));
    dispatch(interviewActions.clearQuestionAudio(currentQuestionIndex));
    audioChunksRef.current = [];
    streamRef.current = null;
    recordingBlobRef.current = null;
  };

  const OverwriteConfirmationModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className={`${theme.card} rounded-xl shadow-2xl border p-6 max-w-md mx-4`}
      >
        <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>
          Replace Existing Recording?
        </h3>
        <p className={`${theme.textSecondary} mb-6`}>
          You already have a recording for this question. Starting a new
          recording will permanently delete the previous one. This action cannot
          be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancelOverwrite}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${theme.buttonSecondary} ${theme.text}`}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmOverwrite}
            className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-medium transition-all duration-200 transform hover:scale-105"
          >
            Replace Recording
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${theme.bg} transition-all duration-300`}>
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-6xl mx-auto">
          <BackButton onClick={handlePrevQuestion} />
          {/* Progress Header */}
          <div className={`${theme.card} rounded-xl shadow-lg border p-6 mb-6`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <div className={`text-2xl font-bold ${theme.text}`}>
                  Question {currentQuestionIndex + 1} of {questions.length}
                </div>
                {currentQuestion && (
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      currentQuestion?.difficulty === "Easy"
                        ? "bg-green-100 text-green-800"
                        : currentQuestion?.difficulty === "Medium"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {currentQuestion?.difficulty}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4">
                <div
                  className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    recording.isRecording
                      ? "bg-red-100 text-red-800"
                      : completedQuestions?.includes(currentQuestionIndex)
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {recording.isRecording ? (
                    <>
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                      Recording
                    </>
                  ) : completedQuestions?.includes(currentQuestionIndex) ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Completed
                    </>
                  ) : (
                    <>
                      <Clock className="h-4 w-4 mr-1" />
                      Ready
                    </>
                  )}
                </div>
              </div>
            </div>

            <ProgressBar
              current={currentQuestionIndex + 1}
              total={questions.length}
              completed={completedQuestions.size}
            />
          </div>

          {/* Error Message */}
          {error && (
            <ErrorMessage
              message={error}
              onDismiss={dismissError}
              className="mb-6"
            />
          )}

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Mobile Video Preview */}
            <div className="space-y-6 block md:hidden">
              <VideoPreview
                ref={videoRef}
                cameraEnabled={cameraEnabled}
                micEnabled={micEnabled}
                recording={recording.isRecording}
                onToggleCamera={toggleCamera}
                onToggleMic={toggleMic}
                theme={theme}
              />
            </div>

            {/* Main Content */}
            <div className="xl:col-span-2 space-y-6">
              {/* Question Display */}
              <div className={`${theme.card} rounded-xl shadow-lg border p-6`}>
                <h2
                  className={`text-xl font-semibold ${theme.text} flex items-center mb-4`}
                >
                  <MessageSquare className="h-5 w-5 mr-2 text-blue-600" />
                  Current Question
                </h2>

                <div className="mb-6">
                  <p
                    className={` text-xl leading-relaxed font-medium p-4 ${
                      darkMode ? "text-black" : "text-gray-900"
                    } bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-500`}
                  >
                    {currentQuestion?.question}
                  </p>
                </div>

                {/* Timer */}
                <Timer
                  timeLeft={recording.timeLeft}
                  isRecording={recording.isRecording}
                />

                {/* Controls */}
                <div className="flex flex-wrap justify-center gap-3 mb-6">
                  {/* Start/Stop Recording Button - Always visible */}
                  <div
                    className={`transform transition-all duration-500 notranslate ${
                      // When it's the only button, position it under the timer (center)
                      !(
                        recording.audioUrl ||
                        recording.questionAudioUrls?.[currentQuestionIndex]
                      ) && !analytics.currentQuestionAnalytics
                        ? "w-full justify-center items-center flex"
                        : "flex items-center justify-center transform transition-all duration-300"
                    }`}
                  >
                    <button
                      onClick={
                        recording.isRecording
                          ? handleStopRecording
                          : handleStartRecording
                      }
                      className={`flex items-center justify-center px-6 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 shadow-lg ${
                        recording.isRecording
                          ? "bg-gradient-to-r from-red-500 to-red-600 text-white animate-pulse"
                          : "bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-xl"
                      } ${
                        // Add visual indicator for existing recording (without bounce)
                        (recording.questionAudioUrls?.[currentQuestionIndex] ||
                          recording.audioUrl) &&
                        !recording.isRecording
                          ? "ring-2 ring-orange-400 ring-offset-2"
                          : ""
                      }`}
                    >
                      {recording.isRecording ? (
                        <>
                          <Square className="h-5 w-5 mr-2 animate-pulse" />
                          Stop Recording
                        </>
                      ) : (
                        <>
                          <Play className="h-5 w-5 mr-2" />
                          {recording.questionAudioUrls?.[
                            currentQuestionIndex
                          ] || recording.audioUrl
                            ? "Record Again"
                            : "Start Recording"}
                        </>
                      )}
                    </button>
                  </div>

                  {/* Get Analytics Button - Shows after recording with slide-in animation */}
                  <div
                    className={`transform transition-all duration-700 ${
                      recording.audioUrl ||
                      recording.questionAudioUrls?.[currentQuestionIndex]
                        ? "translate-x-0 opacity-100 scale-100"
                        : "translate-x-12 opacity-0 scale-75 pointer-events-none"
                    }`}
                  >
                    <button
                      onClick={uploadAudioAndGetAnalytics}
                      disabled={
                        loading.isLoading ||
                        !(
                          recording.audioUrl ||
                          recording.questionAudioUrls?.[currentQuestionIndex]
                        )
                      }
                      className={`flex items-center justify-center px-4 py-3 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform shadow-lg hover:scale-105 hover:shadow-xl
                        ${
                          !analytics.currentQuestionAnalytics
                            ? "bg-gradient-to-r from-green-500 to-green-600 text-white"
                            : "bg-gradient-to-r from-gray-400 to-gray-500 text-white"
                        }
                        ${
                          !analytics.currentQuestionAnalytics &&
                          !loading.isLoading &&
                          (recording.audioUrl ||
                            recording.questionAudioUrls?.[currentQuestionIndex])
                            ? "animate-bounce ring-2 ring-green-300 ring-offset-2"
                            : ""
                        }
                      `}
                    >
                      <BarChart3
                        className={`h-5 w-5 mr-2 ${
                          loading.isLoading ? "animate-spin" : ""
                        }`}
                      />
                      {loading.isLoading
                        ? "Analyzing..."
                        : analytics.currentQuestionAnalytics
                        ? "Analytics Ready"
                        : "Get Analytics"}
                    </button>
                  </div>

                  {/* Reset Button - Shows after recording with slide-in animation */}
                  <div
                    className={`transform transition-all duration-700 delay-150 ${
                      recording.audioUrl ||
                      recording.questionAudioUrls?.[currentQuestionIndex]
                        ? "translate-x-0 opacity-100 scale-100"
                        : "translate-x-16 opacity-0 scale-75 pointer-events-none"
                    }`}
                  >
                    <button
                      onClick={handleReset}
                      disabled={
                        !(
                          recording.audioUrl ||
                          recording.questionAudioUrls?.[currentQuestionIndex]
                        )
                      }
                      className="flex items-center px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-medium transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className="h-5 w-5 mr-2 hover:animate-spin" />
                      Reset
                    </button>
                  </div>

                  {/* Next Question Button - Shows after analytics with slide-in and highlight animation */}
                  <div
                    className={`transform transition-all duration-700 delay-150 ${
                      recording.audioUrl ||
                      recording.questionAudioUrls?.[currentQuestionIndex]
                        ? "translate-x-0 opacity-100 scale-100"
                        : "translate-x-16 opacity-0 scale-75 pointer-events-none"
                    }`}
                  >
                    <button
                      onClick={handleNextQuestion}
                      disabled={!analytics.currentQuestionAnalytics}
                      className={`flex items-center px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-medium transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed
                        ${
                          analytics.currentQuestionAnalytics
                            ? "ring-2 ring-purple-300 ring-offset-2 animate-bounce"
                            : ""
                        }
                      `}
                    >
                      <SkipForward className="h-5 w-5 mr-2" />
                      {currentQuestionIndex < questions?.length - 1
                        ? "Next Question"
                        : "Finish Interview"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Audio Playback */}
              {/* {recording.audioUrl && (
                <div
                  className={`${theme.card} rounded-xl shadow-lg border p-6`}
                >
                  <h3
                    className={`font-semibold ${theme.text} mb-4 flex items-center`}
                  >
                    <Volume2 className="h-5 w-5 mr-2" />
                    Recorded Response
                  </h3>
                  <audio controls className="w-full">
                    <source src={recording?.audioUrl} type="audio/webm" />
                  </audio>
                </div>
              )} */}

              {recording.audioUrl && (
                <div
                  className={`${theme.card} rounded-xl shadow-lg border p-6`}
                >
                  <h3
                    className={`font-semibold ${theme.text} mb-4 flex items-center`}
                  >
                    <Volume2 className="h-5 w-5 mr-2" />
                    Recorded Response
                  </h3>
                  <audio controls className="w-full">
                    <source src={recording.audioUrl} type="audio/webm" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </div>

            {/* Desktop Video Preview */}
            <div className="space-y-6 hidden md:block">
              <VideoPreview
                ref={videoRef}
                cameraEnabled={cameraEnabled}
                micEnabled={micEnabled}
                recording={recording.isRecording}
                onToggleCamera={toggleCamera}
                onToggleMic={toggleMic}
                theme={theme}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Add the confirmation modal */}
      {showOverwriteConfirm && <OverwriteConfirmationModal />}

      {loading.isLoading && (
        <LoadingOverlay message={loading.message} darkMode={darkMode} />
      )}
      <ToastContainer />
    </div>
  );
};

export default InterviewSessionPage;
